import { z } from 'zod';
import { MAX_CELL_LENGTH } from './unit-cells.js';
/**
 * The 15 business columns, in the required order (BR-040 as corrected by the product owners):
 * "תמורה/ רוכשים" and "תעודת זהות" are not part of the sheet. Additional parking and storage
 * column pairs are appended after these (`apartmentColumns`).
 */
export const APARTMENT_COLUMNS = [
    { key: 'building', header: 'בנין', secondary: null, group: 'apartment', available: true },
    { key: 'floor', header: 'קומה', secondary: null, group: 'apartment', available: true },
    { key: 'apartmentNumber', header: 'דירה', secondary: null, group: 'apartment', available: true },
    { key: 'modelType', header: 'טיפוס', secondary: null, group: 'apartment', available: true },
    { key: 'contactName', header: 'שם דייר', secondary: null, group: 'contact', available: false },
    { key: 'contactPhone', header: 'טלפון', secondary: null, group: 'contact', available: false },
    {
        key: 'contactPhoneExtra',
        header: 'טלפון נוסף',
        secondary: null,
        group: 'contact',
        available: false,
    },
    { key: 'contactEmail', header: 'מייל', secondary: null, group: 'contact', available: false },
    {
        key: 'contactEmailExtra1',
        header: 'מייל נוסף',
        secondary: 'נוסף 1',
        group: 'contact',
        available: false,
    },
    {
        key: 'contactEmailExtra2',
        header: 'מייל נוסף',
        secondary: 'נוסף 2',
        group: 'contact',
        available: false,
    },
    { key: 'parkingNumber', header: 'חניה', secondary: null, group: 'parking', available: true },
    {
        key: 'parkingLocation',
        header: 'מספר מרתף',
        secondary: 'חניה',
        group: 'parking',
        available: true,
    },
    { key: 'storageNumber', header: 'מחסן', secondary: null, group: 'storage', available: true },
    {
        key: 'storageLocation',
        header: 'מספר מרתף',
        secondary: 'מחסן',
        group: 'storage',
        available: true,
    },
    { key: 'remarks', header: 'הערות', secondary: null, group: 'remarks', available: false },
];
/** Longest original apartment number ("מס' דירה מקורי"). */
export const SOURCE_NUMBER_MAX = 32;
/**
 * The apartment's source identity (DR-183), after the 15 business columns (so the workbook layout
 * never moves) and before any additional parking and storage pairs:
 * - "מס' דירה מקורי": the apartment's original number in the source, unique in the project. It is
 *   set once (at creation, or later while empty) and never changed or removed.
 * - "קומה במקור": the floor exactly as the source states it, when it differs from the floor's own
 *   label (a duplex starting on the ground floor: "קרקע+1"). It does not change the floor.
 */
export const APARTMENT_SOURCE_COLUMNS = [
    {
        key: 'sourceNumber',
        header: "מס' דירה מקורי",
        secondary: null,
        group: 'source',
        available: true,
    },
    {
        key: 'sourceFloorLabel',
        header: 'קומה במקור',
        secondary: null,
        group: 'source',
        available: true,
    },
];
/**
 * The apartment's restricted fields, each behind its own keys:
 * - the source-listed contact (one person or name cell, as written in the source; DR-153,
 *   DR-185): personal data, read only with `projects.contact.view` and written only with
 *   `projects.contact.edit`. No identity number.
 * - the apartment's note ("הערות", DR-187): operational, read with `projects.note.view` and
 *   written with `projects.note.edit`, independently of the contact keys.
 */
export const APARTMENT_CONTACT_FIELDS = [
    'contactName',
    'contactPhone',
    'contactPhoneExtra',
    'contactEmail',
    'contactEmailExtra1',
    'contactEmailExtra2',
];
export const APARTMENT_PERSONAL_FIELDS = [...APARTMENT_CONTACT_FIELDS, 'remarks'];
export const PERSONAL_FIELD_LIMITS = {
    contactName: 200,
    contactPhone: 60,
    contactPhoneExtra: 60,
    contactEmail: 320,
    contactEmailExtra1: 320,
    contactEmailExtra2: 320,
    remarks: 2000,
};
export const isPersonalField = (key) => APARTMENT_PERSONAL_FIELDS.includes(key);
export const APARTMENT_NOTE_MAX = PERSONAL_FIELD_LIMITS.remarks;
export const restrictedGroupOf = (field) => field === 'remarks' ? 'note' : 'contact';
/** True when `key` is a restricted field of a group `access` allows. */
export const mayAccessField = (key, access) => isPersonalField(key) && access[restrictedGroupOf(key)] === true;
/** The apartment's own fields this phase writes; only these may ever be sent as cells. */
export const APARTMENT_CELL_FIELDS = [
    'building',
    'floor',
    'apartmentNumber',
    'modelType',
    'sourceNumber',
    'sourceFloorLabel',
];
/**
 * Column key of one parking or storage slot. Slot 0 is the default pair ("חניה" / "מספר מרתף");
 * slot n ≥ 1 is the n-th additional pair. Each slot of a row holds one allocation of that row.
 */
export function slotKey(kind, part, slot) {
    const base = `${kind}${part === 'number' ? 'Number' : 'Location'}`;
    return slot === 0 ? base : `${base}_${String(slot)}`;
}
const SLOT_KEY = /^(parking|storage)(Number|Location)(?:_([1-9]\d{0,2}))?$/;
export function parseSlotKey(key) {
    const match = SLOT_KEY.exec(key);
    if (match === null)
        return null;
    return {
        kind: match[1],
        part: match[2] === 'Number' ? 'number' : 'location',
        slot: match[3] === undefined ? 0 : Number(match[3]),
    };
}
/**
 * The writable column keys: the apartment's own fields and every slot column, and the restricted
 * columns of each group the reader may see (`access`).
 */
export const isAvailableColumn = (key, access = {}) => APARTMENT_CELL_FIELDS.includes(key) ||
    parseSlotKey(key) !== null ||
    mayAccessField(key, access);
/** One additional pair of columns ("חניה נוספת 1" / "קומת חניה נוספת 1"). */
export function extraSlotColumns(kind, slot) {
    const n = String(slot);
    return kind === 'parking'
        ? [
            {
                key: slotKey(kind, 'number', slot),
                header: `חניה נוספת ${n}`,
                secondary: null,
                group: 'parking',
                available: true,
            },
            {
                key: slotKey(kind, 'location', slot),
                header: `קומת חניה נוספת ${n}`,
                secondary: null,
                group: 'parking',
                available: true,
            },
        ]
        : [
            {
                key: slotKey(kind, 'number', slot),
                header: `מחסן נוסף ${n}`,
                secondary: null,
                group: 'storage',
                available: true,
            },
            {
                key: slotKey(kind, 'location', slot),
                header: `קומת מחסן נוספת ${n}`,
                secondary: null,
                group: 'storage',
                available: true,
            },
        ];
}
/**
 * The sheet's columns: the 15 business columns, the 2 source-identity columns, then the
 * additional parking pairs, then the additional storage pairs. Everything is appended after the
 * 15 columns, so the layout that users paste from their workbooks never moves.
 */
export function apartmentColumns(extra, access = {}) {
    const pairs = (kind, count) => Array.from({ length: count }, (_, i) => extraSlotColumns(kind, i + 1)).flat();
    return [
        ...APARTMENT_COLUMNS.map((column) => mayAccessField(column.key, access) ? { ...column, available: true } : column),
        ...APARTMENT_SOURCE_COLUMNS,
        ...pairs('parking', extra.parking),
        ...pairs('storage', extra.storage),
    ];
}
// ---------------------------------------------------------------------------------------------
// Older layouts. The original workbook has 17 columns: the 15 above plus "תמורה/ רוכשים"
// (position 5) and "תעודת זהות" (position 7). The previous sheet layout had 16: the 15 plus
// "תעודת זהות" (position 6). Their extra columns are dropped only after the user confirms.
export const EXCLUDED_SOURCE_HEADER = 'תמורה/ רוכשים';
export const NATIONAL_ID_HEADER = 'תעודת זהות';
export const LEGACY_LAYOUTS = {
    source: { width: 17, drop: [4, 6], dropped: [EXCLUDED_SOURCE_HEADER, NATIONAL_ID_HEADER] },
    previous: { width: 16, drop: [5], dropped: [NATIONAL_ID_HEADER] },
};
const normalizeHeader = (text) => text
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*\/\s*/g, '/');
const SHEET_HEADERS = APARTMENT_COLUMNS.map((column) => normalizeHeader(column.header));
/** The sheet's header row with the source-identity columns (a copy of the sheet itself). */
const SHEET_WITH_SOURCE_HEADERS = [
    ...SHEET_HEADERS,
    ...APARTMENT_SOURCE_COLUMNS.map((column) => normalizeHeader(column.header)),
];
const withInserted = (headers, inserts) => {
    const result = [...headers];
    for (const [index, header] of inserts)
        result.splice(index, 0, normalizeHeader(header));
    return result;
};
const LAYOUT_HEADERS = {
    sheet: SHEET_HEADERS,
    previous: withInserted(SHEET_HEADERS, [[5, NATIONAL_ID_HEADER]]),
    source: withInserted(SHEET_HEADERS, [
        [4, EXCLUDED_SOURCE_HEADER],
        [6, NATIONAL_ID_HEADER],
    ]),
};
/**
 * Whether a pasted row is the header row of the current sheet (with or without its source-identity
 * columns) or of an older layout.
 */
export function headerRowLayout(cells) {
    const normalized = cells.map(normalizeHeader);
    const matches = (headers) => normalized.length === headers.length && headers.every((h, i) => normalized[i] === h);
    for (const layout of ['source', 'previous', 'sheet']) {
        if (matches(LAYOUT_HEADERS[layout]))
            return layout;
    }
    return matches(SHEET_WITH_SOURCE_HEADERS) ? 'sheet' : null;
}
/** Removes an older layout's extra columns from every row. Their values are not kept anywhere. */
export const dropLegacyColumns = (table, layout) => table.map((row) => row.filter((_, index) => !LEGACY_LAYOUTS[layout].drop.includes(index)));
// ---------------------------------------------------------------------------------------------
// Read model: `GET /projects/:projectId/apartment-sheet`.
const space = z
    .object({
    allocationId: z.uuid(),
    spaceId: z.uuid(),
    number: z.string(),
    /** The space's level label, or '' when it has no level. */
    location: z.string(),
    structureCode: z.string(),
    /**
     * The column pair that shows it (0 = the default pair): the pair it was entered in. Earlier
     * pairs may be empty.
     */
    slot: z.number().int().min(0).max(49),
})
    .strict();
export const apartmentSheetRowSchema = z
    .object({
    unitId: z.uuid(),
    rowVersion: z.number().int().min(1),
    building: z.string(),
    /** The level label ('' when the apartment has no floor). */
    floor: z.string(),
    apartmentNumber: z.string(),
    modelType: z.string().nullable(),
    /** The original apartment number (set once), and the floor as the source states it. */
    sourceNumber: z.string().nullable(),
    sourceFloorLabel: z.string().nullable(),
    /**
     * Restricted fields: null unless the reader may see a group; the fields of a group it may not
     * see are null (contacts: `projects.contact.view`, DR-185; the note: `projects.note.view`).
     */
    personal: z
        .object(Object.fromEntries(APARTMENT_PERSONAL_FIELDS.map((f) => [f, z.string().nullable()])))
        .strict()
        .nullable(),
    /** Current allocations (in effect now), oldest first. */
    parking: z.array(space),
    storage: z.array(space),
})
    .strict();
export const apartmentSheetResponseSchema = z
    .object({
    items: z.array(apartmentSheetRowSchema),
    /** What the reader may do with the contact columns (DR-185). */
    personal: z.object({ view: z.boolean(), edit: z.boolean() }).strict(),
    /** What the reader may do with the note column (DR-187). */
    note: z.object({ view: z.boolean(), edit: z.boolean() }).strict(),
    page: z
        .object({
        limit: z.number().int(),
        offset: z.number().int(),
        total: z.number().int(),
    })
        .strict(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Batch preview and commit (DR-147, DR-152). Cells carry only the available fields; a blank or
// absent cell means "no change". Clearing is explicit (`clear`), and every allocation that would
// end must be listed in `confirmedEnds` before the commit writes anything.
export const APARTMENT_BATCH_MAX_ROWS = 500;
export const APARTMENT_CLEARABLE = [
    'floor',
    'modelType',
    'sourceFloorLabel',
    ...APARTMENT_PERSONAL_FIELDS,
];
const cells = z
    .object({
    ...Object.fromEntries(APARTMENT_CELL_FIELDS.map((field) => [field, z.string().max(MAX_CELL_LENGTH).optional()])),
    ...Object.fromEntries(APARTMENT_PERSONAL_FIELDS.map((field) => [
        field,
        z
            .string()
            .max(PERSONAL_FIELD_LIMITS[field] + 50)
            .optional(),
    ])),
})
    .strict();
/**
 * A change to one parking or storage slot of the row: `allocationId` names the allocation the slot
 * showed (null for an empty slot). A blank or absent number / location keeps the stored value;
 * `clear` ends the allocation. `slot` is the column pair, for error reporting only.
 */
const slotChange = z
    .object({
    slot: z.number().int().min(0).max(999),
    allocationId: z.uuid().nullable(),
    number: z.string().max(MAX_CELL_LENGTH).optional(),
    location: z.string().max(MAX_CELL_LENGTH).optional(),
    clear: z.literal(true).optional(),
})
    .strict();
const slots = z
    .object({
    parking: z.array(slotChange).max(200).optional(),
    storage: z.array(slotChange).max(200).optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    for (const kind of ['parking', 'storage']) {
        const list = value[kind] ?? [];
        const ids = list.flatMap((s) => (s.allocationId === null ? [] : [s.allocationId]));
        if (new Set(list.map((s) => s.slot)).size !== list.length || new Set(ids).size !== ids.length)
            ctx.addIssue({ code: 'custom', path: [kind], message: 'duplicate_slot' });
    }
});
const clientRowId = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);
const clear = z
    .array(z.enum(APARTMENT_CLEARABLE))
    .max(APARTMENT_CLEARABLE.length)
    .refine((list) => new Set(list).size === list.length, 'duplicate');
export const apartmentBatchRowSchema = z.discriminatedUnion('op', [
    z.object({ op: z.literal('create'), clientRowId, cells, slots: slots.default({}) }).strict(),
    z
        .object({
        op: z.literal('update'),
        clientRowId,
        unitId: z.uuid(),
        rowVersion: z.number().int().min(1),
        cells,
        clear: clear.default([]),
        slots: slots.default({}),
    })
        .strict(),
]);
export const apartmentBatchRequestSchema = z
    .object({
    batchId: z.uuid(),
    rows: z.array(apartmentBatchRowSchema).min(1).max(APARTMENT_BATCH_MAX_ROWS),
    /** Allocation ids whose ending (including transfers) the user explicitly confirmed. */
    confirmedEnds: z
        .array(z.uuid())
        .max(2 * APARTMENT_BATCH_MAX_ROWS)
        .default([]),
})
    .strict()
    .superRefine((batch, ctx) => {
    const seenRows = new Set();
    const seenUnits = new Set();
    batch.rows.forEach((row, index) => {
        if (seenRows.has(row.clientRowId))
            ctx.addIssue({
                code: 'custom',
                path: ['rows', index, 'clientRowId'],
                message: 'duplicate_row',
            });
        seenRows.add(row.clientRowId);
        if (row.op === 'update') {
            if (seenUnits.has(row.unitId))
                ctx.addIssue({
                    code: 'custom',
                    path: ['rows', index, 'unitId'],
                    message: 'duplicate_row',
                });
            seenUnits.add(row.unitId);
        }
    });
});
export const APARTMENT_ROW_STATUSES = [
    'create',
    'update',
    'unchanged',
    'confirm',
    'invalid',
    'duplicate',
    'conflict',
];
export const ALLOCATION_ACTIONS = ['retain', 'assign', 'end'];
/** Why an allocation ends: replaced by another space, moved to another apartment, cleared. */
export const ALLOCATION_END_REASONS = ['replaced', 'transferred', 'cleared'];
const apartmentRef = z
    .object({ unitId: z.uuid().nullable(), building: z.string(), number: z.string() })
    .strict();
export const allocationOperationSchema = z
    .object({
    kind: z.enum(['parking', 'storage']),
    action: z.enum(ALLOCATION_ACTIONS),
    /** For `end` and `retain`: the existing allocation. */
    allocationId: z.uuid().nullable(),
    /** For `assign` to a space this batch creates, `id` is null and `newSpace` is true. */
    space: z
        .object({
        id: z.uuid().nullable(),
        number: z.string(),
        location: z.string(),
        structureCode: z.string(),
    })
        .strict(),
    /** The apartment the operation applies to (for a transfer's `end`: the apartment losing it). */
    apartment: apartmentRef,
    newSpace: z.boolean(),
    endReason: z.enum(ALLOCATION_END_REASONS).nullable(),
    /** For `end`: whether its id is in `confirmedEnds`. */
    confirmed: z.boolean(),
})
    .strict();
export const apartmentBatchResponseSchema = z
    .object({
    batchId: z.uuid(),
    committed: z.boolean(),
    replayed: z.boolean(),
    summary: z
        .object({
        ...Object.fromEntries(APARTMENT_ROW_STATUSES.map((s) => [s, z.number().int().min(0)])),
        assign: z.number().int().min(0),
        end: z.number().int().min(0),
        retain: z.number().int().min(0),
        /** Spaces the batch creates. */
        spaces: z.number().int().min(0),
    })
        .strict(),
    rows: z.array(z
        .object({
        clientRowId,
        status: z.enum(APARTMENT_ROW_STATUSES),
        unitId: z.uuid().nullable(),
        rowVersion: z.number().int().min(1).nullable(),
        /** Field → error code (APARTMENT_ERROR_MESSAGES). */
        fields: z.record(z.string(), z.string()),
        allocations: z.array(allocationOperationSchema),
    })
        .strict()),
})
    .strict();
/** Hebrew messages for error codes (UI only; the server sends codes). */
export const APARTMENT_ERROR_MESSAGES = {
    required: 'שדה חובה. הזינו ערך.',
    too_long: 'הערך ארוך מדי: עד 12 תווים למספר דירה או מקום, עד 32 למספר דירה מקורי, ועד 40 למרתף או לקומה.',
    invalid: 'ערך לא תקין.',
    unknown_building: 'הבניין אינו קיים בפרויקט. בדקו את קוד הבניין (למשל A).',
    unknown_floor: 'הקומה אינה קיימת בבניין. הזינו מספר קומה או שם קומה קיים.',
    ambiguous_floor: 'הקומה מתאימה ליותר מקומה אחת בבניין. הזינו את שם הקומה המדויק.',
    duplicate_existing: 'כבר קיימת דירה עם מספר זה בבניין. בדקו את מספר הדירה.',
    duplicate_batch: 'אותה דירה מופיעה ביותר משורה אחת בשינויים. השאירו שורה אחת.',
    source_taken: 'מספר הדירה המקורי כבר שייך לדירה אחרת בפרויקט. בדקו את המספר.',
    source_duplicate_batch: 'אותו מספר דירה מקורי הוזן ביותר משורה אחת בשינויים. בדקו את המספרים.',
    source_immutable: 'מספר הדירה המקורי נקבע פעם אחת ואינו ניתן לשינוי או למחיקה.',
    code_taken: 'הקוד הפנימי שנוצר למקום או לדירה כבר קיים בפרויקט. בדקו את המספר.',
    stale: 'משתמש אחר עדכן את השורה בינתיים. בחרו אם להחיל את השינוי שלכם על הגרסה העדכנית.',
    missing: 'הדירה כבר אינה קיימת בפרויקט. טענו את הגיליון מחדש.',
    not_clearable: 'לא ניתן לרוקן שדה זה.',
    not_permitted: 'אין לך הרשאה לשינוי זה.',
    space_not_found: 'לא נמצא מקום עם המספר הזה. בדקו את המספר, או הזינו מספר מרתף קיים כדי ליצור מקום חדש.',
    space_ambiguous: 'יש יותר ממקום אחד עם המספר הזה (במרתפים או בחניונים שונים). הזינו את מספר המרתף.',
    space_location_ambiguous: 'המרתף שצוין קיים ביותר מחניון או ממחסן אחד, ולכן לא ניתן ליצור כאן מקום חדש. צרו אותו בגיליון החניות או המחסנים.',
    space_other_level: 'מקום עם המספר הזה כבר קיים במרתף אחר באותו מבנה. בדקו את מספר המרתף.',
    unknown_apartment: 'הדירה אינה קיימת בבניין. בדקו את מספר הדירה.',
    unknown_location: 'המבנה אינו קיים בפרויקט. בדקו את קוד החניון או המחסן.',
    unknown_level: 'המרתף או הקומה אינם קיימים במבנה. בדקו את המספר.',
    space_scheduled: 'למקום יש הצמדה עתידית מתוכננת, ושינוי שלה עדיין אינו נתמך כאן.',
    space_duplicate_batch: 'אותו מקום הוזן ביותר מתא אחד בשינויים. השאירו אותו בתא אחד בלבד.',
    location_without_number: 'הוזן מספר מרתף בלי מספר מקום. הזינו את מספר החניה או המחסן, או רוקנו את תא המרתף.',
    allocation_changed: 'ההצמדה בעמודה זו השתנתה בינתיים. טענו את הגיליון מחדש ובדקו שוב.',
    already_allocated: 'המקום כבר מוצמד לדירה זו בעמודה אחרת. אין צורך להזין אותו שוב.',
    invalid_slot: 'ערך לא תקין בעמודת החניה או המחסן.',
    scheduled_end: 'להצמדה הנוכחית כבר נקבע מועד סיום, ולכן לא ניתן לשנות אותה כאן.',
};
//# sourceMappingURL=apartment-sheet.js.map